// The "What's new" popup (shared/whatsnew.ts holds the entries and how to add one). It opens once per entry,
// when the game loads after an update, and only when nothing else is asking for the player first (the
// first-time pickers, the tutorial, another dialog). Closing it remembers the entry on the account.
import { latestNews, NEWS_SAMPLE, type NewsArt, type NewsEntry } from '../shared/whatsnew.ts';
import type { ClientMsg } from '../shared/protocol.ts';
import { S } from './state.ts';
import { artInfo, spriteUrl } from './assets.ts';
import { mySprite } from './trainerart.ts';
import { dialogOpen } from './dialog.ts';
import { esc } from './fmt.ts';
import { go } from './router.ts';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const day = (d: string) => { const [, m, dd] = d.split('-').map(Number); return m && dd ? `${dd} ${MONTHS[m - 1]}` : ''; };

function artSrc(a: NewsArt): string {
  if ('creature' in a) return spriteUrl(a.creature);
  if ('outfit' in a) return spriteUrl(`${mySprite(a.outfit)}-down-0`);
  // The largest drawing there is, so small icons stay crisp when shown big.
  const big = Math.max(0, ...(artInfo(a.art)?.sizes ?? []));
  return big > (artInfo(a.art)?.w ?? 0) ? `art/${a.art}@${big}.png` : spriteUrl(a.art);
}

function html(e: NewsEntry): string {
  const hero = `image-set(url(art/${e.hero}.png) 1x, url(art/${e.hero}@2x.png) 2x)`;
  const cards = e.features.map((f, i) => {
    const tag = f.go ? 'button' : 'div';
    return `<${tag} class="wn-card"${f.go ? ` type="button" data-wn-go="${i}"` : ''}>
      <span class="wn-pic"><img src="${artSrc(f.art)}" alt=""></span>
      <span class="wn-txt"><b>${esc(f.title)}</b><span>${esc(f.text)}</span></span></${tag}>`;
  }).join('');
  return `<div class="wn" role="dialog" aria-modal="true" aria-labelledby="wn-title">
    <div class="wn-hero" style="background-image:${hero}">
      ${e.heroArt ? `<img class="wn-hero-pic" src="${artSrc(e.heroArt)}" alt="">` : ''}
      <div class="wn-head"><span class="wn-kick">New${day(e.date) ? ` · ${day(e.date)}` : ''}</span>
        <h2 id="wn-title">${esc(e.title)}</h2>${e.tagline ? `<p>${esc(e.tagline)}</p>` : ''}</div>
    </div>
    <div class="wn-cards n${Math.min(e.features.length, 4)}">${cards}</div>
    <div class="wn-foot"><button class="btn gold" type="button" data-wn-close>Continue</button></div>
  </div>`;
}

let open: HTMLElement | null = null;

/** Show an entry. `seen` runs when it closes (not for an Admin preview of the sample). */
export function showNews(e: NewsEntry, seen?: () => void) {
  if (open) return;
  const before = document.activeElement as HTMLElement | null;
  const scrim = document.createElement('div');
  scrim.className = 'scrim wn-scrim';
  scrim.innerHTML = html(e);
  document.body.append(scrim);
  open = scrim;
  const close = (to?: string) => {
    document.removeEventListener('keydown', onKey, true);
    scrim.remove(); open = null;
    seen?.();
    if (to) go(to); else before?.focus?.();
  };
  const onKey = (ev: KeyboardEvent) => {
    if (ev.key === 'Escape' || ev.key === 'Enter') { ev.preventDefault(); ev.stopImmediatePropagation(); close(); }
    else if (ev.key === 'Tab') {
      const f = [...scrim.querySelectorAll<HTMLElement>('button')];
      const i = f.indexOf(document.activeElement as HTMLElement);
      if (ev.shiftKey && i <= 0) { ev.preventDefault(); f[f.length - 1].focus(); }
      else if (!ev.shiftKey && i === f.length - 1) { ev.preventDefault(); f[0].focus(); }
    }
  };
  document.addEventListener('keydown', onKey, true);
  scrim.addEventListener('click', (ev) => {
    const t = ev.target as HTMLElement;
    if (t === scrim || t.closest('[data-wn-close]')) return close();
    const g = t.closest<HTMLElement>('[data-wn-go]');
    if (g) close(e.features[Number(g.dataset.wnGo)]?.go);
  });
  scrim.querySelector<HTMLElement>('[data-wn-close]')?.focus();
}

/** Admin preview: the newest entry, or the layout sample while there is none. Marks nothing seen. */
export function previewNews() { showNews(latestNews() ?? NEWS_SAMPLE); }

/** On every hello: the newest entry, once, when the player is free to read it. */
export function maybeShowNews(send: (m: ClientMsg) => void) {
  const e = latestNews(), t = S.trainer;
  if (!e || !t || t.settings.newsSeen === e.id) return;
  // The first-time pickers and tutorial come first; the next load tries again.
  if (t.tutorial || t.starterPick || t.namePick || t.lookPick) return;
  window.setTimeout(() => {
    if (open || dialogOpen() || document.querySelector('.scrim') || S.trainer?.settings.newsSeen === e.id) return;
    showNews(e, () => {
      if (S.trainer) S.trainer.settings.newsSeen = e.id;
      send({ t: 'settings', settings: { newsSeen: e.id } });
    });
  }, 800);
}
